import React, { useEffect, useMemo, useState } from 'react';
import { mdiMapClockOutline } from '@mdi/js';
import Icon from '@mdi/react';
import {
  InfoOutlined,
  KeyboardVoiceOutlined,
  SpeakerOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import { Box, Card, Divider, MenuItem, Stack, Switch, Typography } from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/system';
import moment from 'moment-timezone';
import {
  useGetActiveEmailCampaigns,
  useGetUserSettings,
  useUpdateUser,
  useUpdateUserSettings,
} from '@juno/client-api';
import {
  ActiveCampaign,
  FeatureConfigTypeEnum,
  JunoUser,
  Type8c9Enum,
  UserSettings as UserSettingsModel,
} from '@juno/client-api/model';
import { Container } from '@juno/ui';
import {
  FEATURE_FLAG,
  MutationAction,
  SelectMenuProps,
  StyledSelect,
  onMutation,
  useFeatureFlagWithUser,
  useSettings,
} from '@juno/utils';
import { ExtendedThemeConfig } from '../Admin/Navigation';
import PreferencesCard from './PreferencesCard';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 518,
  },
});

const UserSettings: React.FC = () => {
  const { site, user, configs } = useSettings();
  const [micSelectState, setMicSelectState] = useState('');
  const [cameraSelectState, setCameraSelectState] = useState('');
  const [speakerSelectState, setSpeakerSelectState] = useState('');
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [settings, setSettings] = useState({} as UserSettingsModel);
  const [timezone, setTimezone] = useState<string>('');
  const [availableTimezones, setAvailableTimezones] = useState<string[]>([]);
  const [isSavingTimezone, setIsSavingTimezone] = useState<boolean>(false);

  const userAndProfileConfig = configs?.find(
    (config) => config.type === FeatureConfigTypeEnum.user_and_profile,
  ) as ExtendedThemeConfig;

  const isSiteLevelEmailAddressVisibilityEnabled =
    userAndProfileConfig?.config?.enable_emails || false;
  const isSiteLevelMessengerEnabled = userAndProfileConfig?.config?.enable_messenger || false;

  const { data: userSettings, refetch } = useGetUserSettings(
    site?.id || '',
    {
      filter: { user__id: user?.id },
    },
    { query: { enabled: !!site?.id && !!user?.id } },
  );
  const { data: activeEmailCampaigns, isLoading: areActiveEmailCampaignsLoading } =
    useGetActiveEmailCampaigns(site?.id || '', { query: { enabled: !!site?.id && !!user?.id } });

  const isReminderCampaign = (campaign: ActiveCampaign) => {
    return (
      campaign.type === Type8c9Enum.EVENT_REMINDERS_15_MIN ||
      campaign.type === Type8c9Enum.EVENT_REMINDERS_72_HR
    );
  };

  const hasReminderCampaigns = useMemo(() => {
    if (!activeEmailCampaigns) {
      return false;
    }

    return activeEmailCampaigns?.some((campaign) => isReminderCampaign(campaign));
  }, [activeEmailCampaigns]);

  const hasDailyDigest = useMemo(() => {
    if (!activeEmailCampaigns) {
      return false;
    }
    return (
      activeEmailCampaigns?.find((campaign) => campaign.type === Type8c9Enum.DAILY_DIGEST) !==
      undefined
    );
  }, [activeEmailCampaigns]);

  const updateSettings = useUpdateUserSettings(onMutation(MutationAction.UPDATE, '', refetch));
  const updateUser = useUpdateUser(
    onMutation(MutationAction.UPDATE, 'Timezone', () => {
      setIsSavingTimezone(false);
    }),
  );
  const { value: sessionReminderFeatureFlag, loading: sessionReminderFeatureFlagLoading } =
    useFeatureFlagWithUser(FEATURE_FLAG.SESSION_REMINDER);
  const sessionReminderEnabled = sessionReminderFeatureFlag && !sessionReminderFeatureFlagLoading;

  const handleChangeDigest = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSettings({ ...settings, receive_daily_digest: checked });
    updateSettings.mutate({
      siteId: site?.id || '',
      data: {
        ...settings,
        receive_daily_digest: checked,
      },
    });
  };

  const handleChangeEventReminder = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setSettings({ ...settings, receive_event_reminders: checked });
    updateSettings.mutate({
      siteId: site?.id || '',
      data: {
        ...settings,
        receive_event_reminders: checked,
      },
    });
  };

  const handleChangeProfile = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSettings({ ...settings, private_profile: checked });
    updateSettings.mutate({
      siteId: site?.id || '',
      data: {
        ...settings,
        private_profile: checked,
      },
    });
  };
  const handleChangeHiddenEmail = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setSettings({ ...settings, hidden_email: checked });
    updateSettings.mutate({
      siteId: site?.id || '',
      data: {
        ...settings,
        hidden_email: checked,
      },
    });
  };
  const handleChangeMessaging = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSettings({ ...settings, disabled_private_messaging: !checked });
    updateSettings.mutate({
      siteId: site?.id || '',
      data: {
        ...settings,
        disabled_private_messaging: !checked,
      },
    });
  };

  const updateMicrophone = (e: any) => {
    setMicSelectState(e.target.value);
    localStorage.setItem('micChoice', JSON.stringify(e.target.value));
  };

  const updateSpeakers = (e: any) => {
    setSpeakerSelectState(e.target.value);
    localStorage.setItem('speakerChoice', JSON.stringify(e.target.value));
  };

  const updateCamera = (e: any) => {
    setCameraSelectState(e.target.value);
    localStorage.setItem('cameraChoice', JSON.stringify(e.target.value));
  };

  useEffect(() => {
    if (userSettings) {
      setSettings(userSettings.find((us) => us.user === user?.id) || ({} as UserSettingsModel));
    }
  }, [userSettings]);

  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter((device) => device.kind === 'audioinput');
        const cameras = devices.filter((device) => device.kind === 'videoinput');
        const speakers = devices.filter((device) => device.kind === 'audiooutput');

        setMicrophones(microphones);
        setCameras(cameras);
        setSpeakers(speakers);

        const storedMicChoice = localStorage.getItem('micChoice');
        const storedCameraChoice = localStorage.getItem('cameraChoice');
        const storedSpeakerChoice = localStorage.getItem('speakerChoice');

        setMicSelectState(
          storedMicChoice ? JSON.parse(storedMicChoice) : microphones[0]?.deviceId || '',
        );
        setCameraSelectState(
          storedCameraChoice ? JSON.parse(storedCameraChoice) : cameras[0]?.deviceId || '',
        );
        setSpeakerSelectState(
          storedSpeakerChoice ? JSON.parse(storedSpeakerChoice) : speakers[0]?.deviceId || '',
        );
      } catch (err) {
        console.error('Error enumerating devices:', err);
      }
    };

    getDevices();
  }, []);

  // Fetch user's current timezone on component mount
  useEffect(() => {
    if (user?.timezone) {
      setTimezone(user.timezone);
    }

    // Get available timezones from moment-timezone
    setAvailableTimezones(moment.tz.names());
  }, [user]);

  return (
    <Container>
      <Card>
        <Typography variant='h5' fontWeight='600' sx={{ ml: 2, mt: 2 }}>
          Settings
        </Typography>
        <Divider sx={{ mt: 2, mb: 2 }} />
        <Box sx={{ p: 2 }}>
          <PreferencesCard
            handleGoBack={function (): void {
              throw new Error('Function not implemented.');
            }}
          />
          {hasDailyDigest && (
            <Box>
              <Divider sx={{ mt: 3, mb: 3 }} />
              <Box>
                <Stack alignItems='center' direction='row' gap={'2px'}>
                  <Typography variant='h6' fontWeight='600'>
                    Daily Digest Email
                  </Typography>
                  <CustomWidthTooltip
                    placement='right-end'
                    title={
                      <Typography variant='caption' fontSize={'0.7rem'} color={'black'}>
                        I would like to receive information of the recent activity in the last 24
                        hours within discussions I follow, new discussions created globally or for
                        groups I belong to, activity on my posts (likes & replies), when I am
                        mentioned in a post, or upcoming events.
                      </Typography>
                    }
                  >
                    <InfoOutlined />
                  </CustomWidthTooltip>
                </Stack>
                <Box pb={1} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='subtitle2' color={'#525252'}>
                    Notify me by email of recent activity in the last 24 hours
                  </Typography>
                  <Switch
                    checked={
                      settings.receive_daily_digest !== undefined
                        ? settings.receive_daily_digest
                        : true
                    }
                    onChange={handleChangeDigest}
                    name='receive_daily_digest'
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Divider sx={{ mt: 2, mb: 2 }} />
          <Box>
            <Typography variant='h6' fontWeight='600' mb={1}>
              Set Your Timezone
            </Typography>
            {/* Timezone choice */}
            <StyledSelect
              fullWidth
              name='timezone'
              id='timezoneSelect'
              onChange={(e: any) => {
                setIsSavingTimezone(true);
                setTimezone(e.target.value);
                updateUser.mutate({
                  platformId: site?.platform_id || '',
                  userId: user?.id || '',
                  data: {
                    timezone: e.target.value,
                  } as JunoUser,
                });
              }}
              value={timezone}
              size='small'
              startAdornment={
                <Icon path={mdiMapClockOutline} size={1} style={{ marginRight: 5 }} />
              }
              MenuProps={SelectMenuProps}
              disabled={isSavingTimezone}
            >
              {availableTimezones.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </StyledSelect>
          </Box>
          {sessionReminderEnabled && hasReminderCampaigns && (
            <Box>
              <Divider sx={{ mt: 3, mb: 3 }} />
              <Box>
                <Stack alignItems='center' direction='row' gap={'2px'}>
                  <Typography variant='h6' fontWeight='600'>
                    Event Reminder Emails
                  </Typography>
                  <CustomWidthTooltip
                    placement='right-end'
                    title={
                      <Typography variant='caption' fontSize={'0.7rem'} color={'black'}>
                        I would like to receive emails for upcoming events that I am attending.
                      </Typography>
                    }
                  >
                    <InfoOutlined />
                  </CustomWidthTooltip>
                </Stack>
                <Box pb={1} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='subtitle2' color={'#525252'}>
                    Notify me by email of upcoming events that I am scheduled to attend
                  </Typography>
                  <Switch
                    checked={
                      settings.receive_event_reminders !== undefined
                        ? settings.receive_event_reminders
                        : true
                    }
                    onChange={handleChangeEventReminder}
                    name='receive_event_reminders'
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Box>
            <Typography variant='h6' fontWeight='600'>
              Privacy
            </Typography>
            <Box pb={1} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant='subtitle2' color={'#525252'}>
                Make my profile private
              </Typography>
              <Switch
                checked={settings.private_profile || false}
                onChange={handleChangeProfile}
                name='private_profile'
              />
            </Box>
            {isSiteLevelEmailAddressVisibilityEnabled && (
              <Box pb={1} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='subtitle2' color={'#525252'}>
                  Hide only my email
                </Typography>
                <Switch
                  checked={settings.hidden_email || false}
                  onChange={handleChangeHiddenEmail}
                  name='hidden_email'
                />
              </Box>
            )}
          </Box>
          {isSiteLevelMessengerEnabled && (
            <>
              <Divider sx={{ mt: 3, mb: 3 }} />
              <Box>
                <Typography variant='h6' fontWeight='600'>
                  Messenger
                </Typography>
                <Box pb={1} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='subtitle2' color={'#525252'}>
                    Allow other users to send me messages
                  </Typography>
                  <Switch
                    checked={!settings.disabled_private_messaging}
                    onChange={handleChangeMessaging}
                    name='disabled_private_messaging'
                  />
                </Box>
              </Box>
            </>
          )}
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Box>
            <Typography variant='h6' fontWeight='600'>
              Camera & Microphone
            </Typography>
            <Stack spacing={0} mt={2} direction={'column'} flexWrap={'wrap'}>
              {/* Microphone choice */}
              <StyledSelect
                fullWidth
                name='micOptions'
                id='micSelect'
                onChange={updateMicrophone}
                value={micSelectState}
                size='small'
                startAdornment={<KeyboardVoiceOutlined sx={{ fontSize: '1.5rem', mr: 0.5 }} />}
                MenuProps={SelectMenuProps}
              >
                {microphones.map((mic) => (
                  <MenuItem key={`mic-${mic.deviceId}`} value={mic.deviceId}>
                    {mic.label || 'Unnamed Microphone'}
                  </MenuItem>
                ))}
              </StyledSelect>
              {/* Camera choice */}
              <StyledSelect
                fullWidth
                name='cameraOptions'
                id='cameraSelect'
                onChange={updateCamera}
                value={cameraSelectState}
                size='small'
                startAdornment={<VideocamOutlined sx={{ fontSize: '1.5rem', mr: 0.5 }} />}
                MenuProps={SelectMenuProps}
              >
                {cameras.map((camera) => (
                  <MenuItem key={`cam-${camera.deviceId}`} value={camera.deviceId}>
                    {camera.label || 'Unnamed Camera'}
                  </MenuItem>
                ))}
              </StyledSelect>
              {/* Speakers choice */}
              <StyledSelect
                fullWidth
                name='speakersOptions'
                id='speakersSelect'
                onChange={updateSpeakers}
                value={speakerSelectState}
                size='small'
                startAdornment={<SpeakerOutlined sx={{ fontSize: '1.5rem', mr: 0.5 }} />}
                MenuProps={SelectMenuProps}
              >
                {speakers.map((speaker) => (
                  <MenuItem key={`speaker-${speaker.deviceId}`} value={speaker.deviceId}>
                    {speaker.label || 'Unnamed Speaker'}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Stack>
          </Box>
        </Box>
      </Card>
    </Container>
  );
};

export default UserSettings;
